/**
 * Small per-browser preferences (WS2).
 *
 * Reader selection and the last book someone was reporting are conveniences,
 * not data: they live in localStorage, keyed by user so a shared browser
 * doesn't hand one person's selection to another.
 *
 * Every access is guarded — localStorage throws in private mode on some
 * browsers, and a missing value must never break the page.
 */

const KEYS = {
  lastReader: 'rtw:lastReader',
  lastBook: 'rtw:lastBook'
};

function read(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key, value) {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, String(value));
    }
  } catch {
    // Storage unavailable — the preference is simply not remembered.
  }
}

const scoped = (base, userId) => `${base}:${userId || 'anon'}`;

/** The reader this user last logged for. */
export function getLastReaderId(userId) {
  return read(scoped(KEYS.lastReader, userId));
}

export function setLastReaderId(userId, readerId) {
  write(scoped(KEYS.lastReader, userId), readerId);
}

/**
 * Pick which reader to show: the remembered one if it still exists, else the
 * primary reader, else the first.
 */
export function pickReader(readers, userId) {
  if (!readers || readers.length === 0) return null;
  const rememberedId = getLastReaderId(userId);
  return readers.find(r => r.id === rememberedId)
    || readers.find(r => r.isPrimary)
    || readers[0];
}

/** The book this user was last selecting chapters from. */
export function getLastBook(userId) {
  return read(scoped(KEYS.lastBook, userId));
}

export function setLastBook(userId, bookName) {
  write(scoped(KEYS.lastBook, userId), bookName);
}
